<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { iSelect, iStepper, iTextInput } from '@inscribe/signal';
import {
  type AmountFilter,
  AmountFieldFilterOperator,
} from '@/types/decision-engine/configurable-insights.types';
import FilterEditorLayout from '@/V2/decision-engine/configurable-insights/components/filters/FilterEditorLayout.vue';
import {
  formatAmountFromCents,
  parseAmountToCents,
  stripAmountGroupSeparators,
  isValidAmountInput,
} from '@/utils/toCurrency';

interface Props {
  filter: AmountFilter | null;
  label: string;
  isPercentage?: boolean;
}

interface Emits {
  (e: 'update:filter', value: AmountFilter): void;
  (e: 'remove'): void;
}

const props = withDefaults(defineProps<Props>(), {
  isPercentage: false,
});
const emit = defineEmits<Emits>();

// Operator options
const operatorOptions = [
  { text: 'Greater than', value: AmountFieldFilterOperator.GREATER_THAN },
  { text: 'Less than', value: AmountFieldFilterOperator.LESS_THAN },
  { text: 'Equal to', value: AmountFieldFilterOperator.EQUAL_TO },
  { text: 'Between', value: AmountFieldFilterOperator.BETWEEN },
];

// Current filter or default
const currentFilter = computed<AmountFilter>(() => {
  return (
    props.filter || {
      operator: AmountFieldFilterOperator.GREATER_THAN,
      values: [0],
    }
  );
});

// Check if operator is BETWEEN (requires 2 values)
const isBetween = computed(
  () => currentFilter.value.operator === AmountFieldFilterOperator.BETWEEN,
);

// Local display values — raw while typing, locale-formatted on focusout
const localValues = ref([formatAmountFromCents(0), formatAmountFromCents(0)]);
const localErrors = ref([false, false]);

// Sync local display from props when: initial load or operator changes
watch(
  () => props.filter,
  (newFilter, oldFilter) => {
    if (!oldFilter || newFilter?.operator !== oldFilter?.operator) {
      const vals = newFilter?.values ?? [0, 0];
      localValues.value[0] = formatAmountFromCents(vals[0] ?? 0);
      localValues.value[1] = formatAmountFromCents(vals[1] ?? 0);
    }
  },
  { immediate: true },
);

// Update handlers
const updateOperator = (operator: AmountFieldFilterOperator) => {
  let newValues: number[];
  if (operator === AmountFieldFilterOperator.BETWEEN) {
    newValues =
      currentFilter.value.values.length >= 2
        ? [currentFilter.value.values[0], currentFilter.value.values[1]]
        : [0, 0];
  } else {
    newValues = [currentFilter.value.values[0] || 0];
  }

  emit('update:filter', {
    operator,
    values: newValues,
  });
};

const updateValue = (value: number, index: number = 0) => {
  const newValues = [...currentFilter.value.values];
  newValues[index] = value;

  emit('update:filter', {
    ...currentFilter.value,
    values: newValues,
  });
};

const updateValueFromText = (value: string, index: number = 0) => {
  localValues.value[index] = value;
  localErrors.value[index] = false; // clear error while typing
  updateValue(parseAmountToCents(value), index);
};

// Strip group separators on focus so the user can type cleanly
const startEditingValue = (index: number) => {
  localValues.value[index] = stripAmountGroupSeparators(
    localValues.value[index],
  );
};

// Validate and reformat on blur; keep raw value visible if invalid
const reformatValue = (index: number) => {
  const raw = localValues.value[index];
  if (!raw || raw.trim() === '') {
    localErrors.value[index] = false;
    return;
  }
  if (!isValidAmountInput(raw)) {
    localErrors.value[index] = true;
    return;
  }
  localErrors.value[index] = false;
  localValues.value[index] = formatAmountFromCents(
    currentFilter.value.values[index] ?? 0,
  );
};
</script>

<template>
  <!-- BETWEEN operator -->
  <FilterEditorLayout
    v-if="isBetween"
    :label="label"
    @remove="emit('remove')"
    class="between-filter-editor"
  >
    <!-- Operator selection -->
    <div class="filter-section">
      <i-select
        :model-value="currentFilter.operator"
        :options="operatorOptions"
        @update:model-value="updateOperator"
      />
    </div>

    <!-- Min and Max values -->
    <div class="d-flex flex-row align-items-start i-gap-3 filter-section">
      <i-stepper
        v-if="isPercentage"
        :model-value="currentFilter.values[0] ?? 0"
        :max-value="100"
        :min-value="0"
        :full-width="true"
        :precision="0"
        :enabled="true"
        size="medium"
        class="between-input"
        @update:model-value="(val) => updateValue(val, 0)"
      />
      <i-text-input
        v-else
        :model-value="localValues[0]"
        :has-error="localErrors[0]"
        hint-text="Enter a valid amount"
        :show-hint-text="localErrors[0]"
        class="between-input"
        data-testid="amount-value-input"
        @update:model-value="(val) => updateValueFromText(val, 0)"
        @focusin="startEditingValue(0)"
        @focusout="reformatValue(0)"
      />

      <div
        class="i-text-body-2 and-container d-flex align-items-center justify-content-center"
      >
        and
      </div>

      <i-stepper
        v-if="isPercentage"
        :model-value="currentFilter.values[1] ?? 0"
        :max-value="100"
        :min-value="0"
        :full-width="true"
        :precision="0"
        :enabled="true"
        size="medium"
        class="between-input"
        @update:model-value="(val) => updateValue(val, 1)"
      />
      <i-text-input
        v-else
        :model-value="localValues[1]"
        :has-error="localErrors[1]"
        hint-text="Enter a valid amount"
        :show-hint-text="localErrors[1]"
        class="between-input"
        data-testid="amount-value-input"
        @update:model-value="(val) => updateValueFromText(val, 1)"
        @focusin="startEditingValue(1)"
        @focusout="reformatValue(1)"
      />
    </div>
  </FilterEditorLayout>

  <!-- Single value operator -->
  <FilterEditorLayout v-else :label="label" @remove="emit('remove')">
    <!-- Operator selection -->
    <div class="filter-section">
      <i-select
        :model-value="currentFilter.operator"
        :options="operatorOptions"
        @update:model-value="updateOperator"
      />
    </div>

    <!-- Single value -->
    <div class="filter-section">
      <i-stepper
        v-if="isPercentage"
        :model-value="currentFilter.values[0] ?? 0"
        :max-value="100"
        :min-value="0"
        :full-width="true"
        :precision="0"
        :enabled="true"
        size="medium"
        @update:model-value="(val) => updateValue(val, 0)"
      />
      <i-text-input
        v-else
        :model-value="localValues[0]"
        :has-error="localErrors[0]"
        hint-text="Enter a valid amount"
        :show-hint-text="localErrors[0]"
        data-testid="amount-value-input"
        @update:model-value="(val) => updateValueFromText(val, 0)"
        @focusin="startEditingValue(0)"
        @focusout="reformatValue(0)"
      />
    </div>
  </FilterEditorLayout>
</template>

<style scoped lang="scss">
.between-filter-editor {
  .between-input {
    width: 140px;
  }

  .and-container {
    width: 30px;
    height: 44px;
  }
}
</style>
